/**
 * ============================================================================
 * Firebase Authentication Service
 * 紫微斗數算命系統 - Firebase 認證服務
 * ============================================================================
 * 功能：
 * 1. Google OAuth 登入
 * 2. Email/Password 註冊與登入
 * 3. 登出功能
 * 4. 認證狀態監聽
 * 5. Firebase ID Token 管理
 * ============================================================================
 */

import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  GoogleAuthProvider,
  User,
  UserCredential,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFirebaseAuth } from '../config/firebase';

/**
 * 認證結果介面
 */
export interface AuthResult {
  success: boolean;
  user?: User;
  idToken?: string;
  error?: string;
}

/**
 * Google OAuth 登入
 */
export const signInWithGoogle = async (): Promise<AuthResult> => {
  try {
    const auth = getFirebaseAuth();
    const provider = new GoogleAuthProvider();

    // 設定語言為繁體中文
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    // 登入
    const result: UserCredential = await signInWithPopup(auth, provider);
    const user = result.user;

    // 取得 ID Token
    const idToken = await user.getIdToken();

    console.log('✅ Google OAuth 登入成功:', user.email);

    return {
      success: true,
      user,
      idToken,
    };
  } catch (error: any) {
    console.error('❌ Google OAuth 登入失敗:', error);

    let errorMessage = 'Google 登入失敗';

    // 處理特定錯誤
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        errorMessage = '登入視窗已關閉';
        break;
      case 'auth/cancelled-popup-request':
        errorMessage = '登入已取消';
        break;
      case 'auth/popup-blocked':
        errorMessage = '登入視窗被瀏覽器封鎖，請允許彈出視窗';
        break;
      case 'auth/unauthorized-domain':
        errorMessage = '此網域未授權，請聯絡管理員';
        break;
      default:
        errorMessage = error.message || '登入失敗';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Email/Password 註冊
 */
export const registerWithEmail = async (
  email: string,
  password: string
): Promise<AuthResult> => {
  try {
    const auth = getFirebaseAuth();

    // 建立帳號
    const result: UserCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = result.user;

    // 取得 ID Token
    const idToken = await user.getIdToken();

    console.log('✅ Email 註冊成功:', user.email);

    return {
      success: true,
      user,
      idToken,
    };
  } catch (error: any) {
    console.error('❌ Email 註冊失敗:', error);

    let errorMessage = '註冊失敗';

    // 處理特定錯誤
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = '此 Email 已被註冊';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Email 格式無效';
        break;
      case 'auth/weak-password':
        errorMessage = '密碼強度不足（至少 6 個字元）';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Email/Password 認證未啟用';
        break;
      default:
        errorMessage = error.message || '註冊失敗';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Email/Password 登入
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<AuthResult> => {
  try {
    const auth = getFirebaseAuth();

    // 登入
    const result: UserCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = result.user;

    // 取得 ID Token
    const idToken = await user.getIdToken();

    console.log('✅ Email 登入成功:', user.email);

    return {
      success: true,
      user,
      idToken,
    };
  } catch (error: any) {
    console.error('❌ Email 登入失敗:', error);

    let errorMessage = '登入失敗';

    // 處理特定錯誤
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'Email 或密碼錯誤';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Email 或密碼錯誤';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Email 格式無效';
        break;
      case 'auth/user-disabled':
        errorMessage = '此帳號已被停用';
        break;
      case 'auth/too-many-requests':
        errorMessage = '登入失敗次數過多，請稍後再試';
        break;
      default:
        errorMessage = error.message || '登入失敗';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * 登出
 */
export const logOut = async (): Promise<AuthResult> => {
  try {
    const auth = getFirebaseAuth();
    await signOut(auth);

    console.log('✅ 登出成功');

    return {
      success: true,
    };
  } catch (error: any) {
    console.error('❌ 登出失敗:', error);

    return {
      success: false,
      error: error.message || '登出失敗',
    };
  }
};

/**
 * 取得當前使用者的 ID Token
 */
export const getCurrentIdToken = async (): Promise<string | null> => {
  try {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;

    if (!user) {
      return null;
    }

    // 取得 ID Token（如果過期會自動刷新）
    const idToken = await user.getIdToken(true);
    return idToken;
  } catch (error) {
    console.error('❌ 取得 ID Token 失敗:', error);
    return null;
  }
};

/**
 * 監聽認證狀態變化
 */
export const onAuthStateChange = (
  callback: (user: User | null) => void
): (() => void) => {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
};

/**
 * 取得當前登入使用者
 */
export const getCurrentUser = (): User | null => {
  const auth = getFirebaseAuth();
  return auth.currentUser;
};

/**
 * 檢查使用者是否已登入
 */
export const isUserSignedIn = (): boolean => {
  const auth = getFirebaseAuth();
  return auth.currentUser !== null;
};

const firebaseAuthService = {
  signInWithGoogle,
  registerWithEmail,
  signInWithEmail,
  logOut,
  getCurrentIdToken,
  onAuthStateChange,
  getCurrentUser,
  isUserSignedIn,
};

export default firebaseAuthService;

// ============================================================
// Email 驗證相關函數（用於新版防濫用註冊流程）
// ============================================================

/**
 * 建立 Firebase Auth Email/Password 帳號並寄送驗證信
 */
export const createFirebaseUserAndSendVerification = async (
  email: string,
  password: string
): Promise<{ success: boolean; user?: User; error?: string }> => {
  try {
    const auth = getFirebaseAuth();
    const credential: UserCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = credential.user;
    await sendEmailVerification(user, {
      url: `${window.location.origin}/dashboard`,
    });
    return { success: true, user };
  } catch (error: any) {
    // 若 Firebase Auth 帳號已存在（可能是重試），不視為致命錯誤
    if (error.code === 'auth/email-already-in-use') {
      return { success: true, error: 'already-exists' };
    }
    console.error('[firebaseAuth] createFirebaseUserAndSendVerification error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 重新寄送 Email 驗證信給目前登入的 Firebase 用戶
 */
export const resendVerificationEmail = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) return { success: false, error: '尚未登入 Firebase' };
    await sendEmailVerification(user, {
      url: `${window.location.origin}/dashboard`,
    });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * 重新整理 Firebase 用戶狀態並回傳最新 ID Token（含 emailVerified）
 * 用於使用者點擊驗證信後，前端取得最新狀態送給後端
 */
export const reloadFirebaseUserAndGetToken = async (): Promise<{
  success: boolean;
  emailVerified: boolean;
  idToken?: string;
  error?: string;
}> => {
  try {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) return { success: false, emailVerified: false, error: '尚未登入 Firebase' };
    await user.reload();
    const refreshedUser = auth.currentUser;
    if (!refreshedUser) return { success: false, emailVerified: false, error: '無法取得用戶狀態' };
    const idToken = await refreshedUser.getIdToken(true);
    return { success: true, emailVerified: refreshedUser.emailVerified, idToken };
  } catch (error: any) {
    return { success: false, emailVerified: false, error: error.message };
  }
};
